import React, { useEffect, useState } from "react";
import { Contrast, BookOpen } from "lucide-react";
import { cx } from "./ui-kit";
import { SocialLinks } from "./SocialLinks";

const SCALE_KEY = "spire-text-scale";
const HC_KEY = "spire-high-contrast";
const ER_KEY = "spire-easy-read";
const SCALES = { 1: "100%", 2: "112.5%", 3: "125%" };

const applyScale = (s) => {
  document.documentElement.style.fontSize = SCALES[s] || "100%";
};
const applyHc = (h) => {
  document.documentElement.classList.toggle("hc", h);
};
const applyEr = (v) => {
  document.documentElement.classList.toggle("er", v);
};

export const AccessibilityBar = () => {
  const [scale, setScale] = useState(1);
  const [hc, setHc] = useState(false);
  const [er, setEr] = useState(false);

  useEffect(() => {
    const parsed = parseInt(localStorage.getItem(SCALE_KEY) || "1", 10);
    const s = [1, 2, 3].includes(parsed) ? parsed : 1;
    const h = localStorage.getItem(HC_KEY) === "1";
    const e = localStorage.getItem(ER_KEY) === "1";
    applyScale(s);
    applyHc(h);
    applyEr(e);
    setScale(s);
    setHc(h);
    setEr(e);
  }, []);

  const chooseScale = (s) => {
    setScale(s);
    applyScale(s);
    localStorage.setItem(SCALE_KEY, String(s));
  };

  const toggleHc = () => {
    const next = !hc;
    setHc(next);
    applyHc(next);
    localStorage.setItem(HC_KEY, next ? "1" : "0");
  };

  const toggleEr = () => {
    const next = !er;
    setEr(next);
    applyEr(next);
    localStorage.setItem(ER_KEY, next ? "1" : "0");
  };

  return (
    <div className="bg-stone-900 text-stone-200" data-testid="accessibility-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-11 flex items-center justify-between gap-4">
        <SocialLinks variant="bar" size="h-7 w-7" iconSize="h-4 w-4" />
        <div className="flex items-center gap-4">
        <div className="flex items-center gap-2" role="group" aria-label="Text size">
          <span className="text-sm text-stone-400 hidden sm:inline">Text size</span>
          {[1, 2, 3].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => chooseScale(s)}
              aria-pressed={scale === s}
              aria-label={`Text size ${s === 1 ? "normal" : s === 2 ? "large" : "largest"}`}
              data-testid={`text-size-${s}`}
              className={cx(
                "flex h-8 min-w-8 items-center justify-center rounded-md px-2 font-semibold leading-none transition-colors",
                scale === s ? "bg-orange-600 text-white" : "text-stone-200 hover:bg-stone-700"
              )}
              style={{ fontSize: s === 1 ? "12px" : s === 2 ? "15px" : "18px" }}
            >
              A
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={toggleHc}
          aria-pressed={hc}
          data-testid="high-contrast-toggle"
          className={cx(
            "inline-flex items-center gap-1.5 rounded-md px-3 h-8 text-sm font-semibold transition-colors",
            hc ? "bg-orange-600 text-white" : "text-stone-200 hover:bg-stone-700"
          )}
        >
          <Contrast className="h-4 w-4" aria-hidden="true" />
          High contrast
        </button>
        <button
          type="button"
          onClick={toggleEr}
          aria-pressed={er}
          data-testid="easy-read-toggle"
          className={cx(
            "inline-flex items-center gap-1.5 rounded-md px-3 h-8 text-sm font-semibold transition-colors",
            er ? "bg-orange-600 text-white" : "text-stone-200 hover:bg-stone-700"
          )}
        >
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          Easy read
        </button>
        </div>
      </div>
    </div>
  );
};
